import { useState, useEffect } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Box, Typography, Grid } from "@mui/material";
import dayjs from "dayjs";

import FilterBtn from "../../../../../Assets/Icons/btn-filters.png";
import FilterBtnUp from "../../../../../Assets/Icons/btn-filters-up.png";
import { useNotification } from "../../../../../Hooks/useNotification";
import { useApiErrorHandler } from "../../../../../Hooks/useApiErrorHandler";
import httpclient from "../../../../../Utils";
import { createFilterHandler } from "../../../../../Utils/FilterUtils";
import CustomLoadingOverlay from "../../../../../Components/CustomLoadingOverlay";
import useTokenRefresh from "../../../../../Hooks/useTokenRefresh";
import { useBranch } from "../../../../../Hooks/useBranch";
import { buildApiQuery } from "../../../../../Utils/apiQueryBuilder";

import { getColumns } from "./columns";
import Filter from "./Filter";

const INITIAL_FILTER_STATE = {
  fullName: "",
  driver_id: "",
  start_date: null,
  end_date: null,
  active_driver: "",
};

const fieldMappings = {
  fullName: ["full_name", "$contains"],
  driver_id: ["driver_id"],
  start_date: ["start_date"],
  end_date: ["end_date"],
  active_driver: ["active_driver"],
};

const valueTransformers = {
  start_date: (value) => (value ? dayjs(value).format("YYYY-MM-DD") : ""),
  end_date: (value) => (value ? dayjs(value).format("YYYY-MM-DD") : ""),
};

export default function DriversReportTable() {
  const [driversReport, setDriversReport] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [driverActiveStatus, setDriverActiveStatus] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterData, setFilterData] = useState(INITIAL_FILTER_STATE);
  const [submittedData, setSubmittedData] = useState({});

  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const [rowCount, setRowCount] = useState(0);
  const [sortModel, setSortModel] = useState([]);
  const [refreshList, setRefreshList] = useState(false);

  const { currentBranch } = useBranch();
  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();
  const { getTokenRefreshed: refresh } = useTokenRefresh();

  const fetchDriversReport = async (
    page = 1,
    pageSize = 20,
    filters = submittedData,
    sortInfo = sortModel
  ) => {
    setLoading(true);

    const endpoint = `delivery/reports/drivers-report`;

    const apiUrl = buildApiQuery(endpoint, {
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
      filters: submittedData,
      sortModel,
      fieldMappings,
      valueTransformers,
    });

    try {
      const res = await httpclient.get(apiUrl);
      const { data } = res;

      if (data.status === 200) {
        setDriversReport(data.data);
        setDrivers(data.drivers || []);
        setDriverActiveStatus(data.driverActiveStatus || []);
        setRowCount(data.meta?.total || data.count || 0);
        setRefreshList(false);
      } else {
        showNotification("error", data.message);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        refresh();
      } else {
        handleApiError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDriversReport(
      paginationModel.page + 1,
      paginationModel.pageSize,
      submittedData,
      sortModel
    );
  }, [
    refreshList,
    currentBranch,
    paginationModel.page,
    paginationModel.pageSize,
    sortModel,
    submittedData,
  ]);

  const handleChangeFilter = (field) => (event, value) => {
    if (
      field.includes("Date") ||
      field.includes("start") ||
      field.includes("end")
    ) {
      setFilterData((prev) => ({
        ...prev,
        [field]: value || (event && event.target.value) || null,
      }));
      return;
    }

    setFilterData((prev) => ({
      ...prev,
      [field]: value || (event && event.target.value) || "",
    }));
  };

  const handleFilter = createFilterHandler({
    setLoading,
    setSubmittedData,
    setResults: setDriversReport,
    showNotification,
    handleApiError,
    httpClient: httpclient,
    endpoint: `delivery/reports/drivers-report`,
    fieldMappings,
    valueTransformers,
    refresh,
  });

  const onFilterClick = () => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    handleFilter(filterData);
  };

  const handleRemove = (field) => {
    const newFilterData = { ...filterData };
    const newSubmittedData = { ...submittedData };

    newFilterData[field] =
      field.includes("start") || field.includes("end") ? null : "";
    delete newSubmittedData[field];

    setFilterData(newFilterData);
    setSubmittedData(newSubmittedData);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));

    handleFilter(newFilterData);
  };

  const handleClearFilters = () => {
    setFilterData(INITIAL_FILTER_STATE);
    setSubmittedData({});
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const columns = getColumns();

  return (
    <Box sx={{ width: "100%", padding: 2 }}>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          backgroundColor: "#fff",
          padding: "24px 16px",
          borderRadius: "8px",
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Drivers Report
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
            gap={2}
          >
            <img
              src={filterOpen ? FilterBtnUp : FilterBtn}
              alt="Filter"
              style={{ cursor: "pointer" }}
              onClick={() => setFilterOpen(!filterOpen)}
            />
          </Grid>
          <Filter
            filterOpen={filterOpen}
            filterData={filterData}
            handleChangeFilter={handleChangeFilter}
            handleFilter={onFilterClick}
            handleClearFilters={handleClearFilters}
            handleRemove={handleRemove}
            submittedData={submittedData}
            drivers={drivers}
            driverActiveStatus={driverActiveStatus}
          />
        </Grid>

        <DataGrid
          rows={driversReport || []}
          columns={columns}
          autoHeight
          disableRowSelectionOnClick
          initialState={{
            pagination: { paginationModel: { pageSize: 20 } },
          }}
          loading={loading}
          slots={{
            loadingOverlay: CustomLoadingOverlay,
          }}
          sx={{
            "& .MuiDataGrid-cell": {
              fontSize: "14px",
              fontWeight: 500,
              color: "#48505E",
            },
          }}
          paginationMode="server"
          sortingMode="server"
          onSortModelChange={handleSortModelChange}
          sortModel={sortModel}
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[20, 50, 100]}
        />
      </Box>
    </Box>
  );
}
